import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  confirmManualPayment,
  listManualPayments,
  rejectManualPayment,
  uploadStatementImport,
  type ReconciliationManualPaymentRow,
} from "../api/endpoints/reconciliation"
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageSection,
  Select,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Textarea,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate, formatDateTime, formatEnumLabel } from "../utils/formatting"

function statusTone(
  status?: string,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "CONFIRMED":
      return "success"
    case "REJECTED":
      return "danger"
    case "PENDING_VERIFICATION":
      return "warning"
    default:
      return "neutral"
  }
}

function reviewTone(
  row: ReconciliationManualPaymentRow,
): "neutral" | "success" | "warning" | "danger" | "info" {
  if (row.status === "CONFIRMED") {
    return "success"
  }
  if (row.status === "REJECTED") {
    return "danger"
  }
  if (Boolean(row.requiresManualReview)) {
    return "danger"
  }
  if (row.candidates.length > 0) {
    return "info"
  }
  return "warning"
}

function reviewLabel(row: ReconciliationManualPaymentRow) {
  if (row.status === "CONFIRMED") {
    return "Matched"
  }
  if (row.status === "REJECTED") {
    return "Rejected"
  }
  if (Boolean(row.requiresManualReview)) {
    return "Needs Review"
  }
  if (row.candidates.length > 0) {
    return "Candidate Found"
  }
  return "Waiting"
}

export function ReconciliationPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [confirmNote, setConfirmNote] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")
  const [selectedStatementRecordId, setSelectedStatementRecordId] = useState("")
  const [feedback, setFeedback] = useState("")

  const page = Number(searchParams.get("page") ?? "1")
  const queue = searchParams.get("queue") ?? ""
  const status = searchParams.get("status") ?? ""
  const search = searchParams.get("search") ?? ""
  const selectedConfirmationId = searchParams.get("selected_confirmation_id")
  const canMutate = user?.role === "SUPER_ADMIN"

  const manualPaymentsQuery = useQuery({
    queryKey: ["reconciliation-manual-payments", page, queue, status, search],
    queryFn: () =>
      listManualPayments({
        page,
        perPage: 20,
        queue: queue || undefined,
        status: status || undefined,
        search: search || undefined,
      }),
  })

  const rows = manualPaymentsQuery.data?.data ?? []
  const meta = manualPaymentsQuery.data?.meta
  const selectedRow = useMemo(
    () => rows.find((row) => row.confirmationId === selectedConfirmationId) ?? null,
    [rows, selectedConfirmationId],
  )

  useEffect(() => {
    if (!selectedRow) {
      setSelectedStatementRecordId("")
      setConfirmNote("")
      setRejectionReason("")
      return
    }

    setSelectedStatementRecordId(
      selectedRow.matchedStatementRecordId ?? selectedRow.candidates[0]?.statementRecordId ?? "",
    )
    setConfirmNote("")
    setRejectionReason(selectedRow.rejectionReason ?? "")
  }, [selectedRow])

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }

    if (key !== "selected_confirmation_id" && key !== "page") {
      next.set("page", "1")
    }

    setSearchParams(next)
  }

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadStatementImport(file),
    onSuccess: async (response) => {
      setFeedback(`Imported ${response.data.rowCount} statement rows.`)
      await queryClient.invalidateQueries({ queryKey: ["reconciliation-manual-payments"] })
    },
    onError: (error) => {
      setFeedback((error as Error).message)
    },
  })

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRow) {
        throw new Error("Select a manual payment confirmation first.")
      }

      return confirmManualPayment(selectedRow.confirmationId, {
        statementRecordId: selectedStatementRecordId || undefined,
        note: confirmNote.trim() || undefined,
      })
    },
    onSuccess: async () => {
      setFeedback("Manual payment confirmed.")
      await queryClient.invalidateQueries({ queryKey: ["reconciliation-manual-payments"] })
      updateParam("selected_confirmation_id", null)
    },
    onError: (error) => {
      setFeedback((error as Error).message)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRow) {
        throw new Error("Select a manual payment confirmation first.")
      }
      if (rejectionReason.trim().length < 3) {
        throw new Error("Enter a rejection reason of at least 3 characters.")
      }

      return rejectManualPayment(selectedRow.confirmationId, rejectionReason.trim())
    },
    onSuccess: async () => {
      setFeedback("Manual payment rejected.")
      await queryClient.invalidateQueries({ queryKey: ["reconciliation-manual-payments"] })
      updateParam("selected_confirmation_id", null)
    },
    onError: (error) => {
      setFeedback((error as Error).message)
    },
  })

  return (
    <div className="space-y-6">
      <PageSection
        title="Reconciliation"
        actions={
          canMutate ? (
            <>
              <input
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    uploadMutation.mutate(file)
                  }
                  event.target.value = ""
                }}
                ref={fileInputRef}
                type="file"
              />
              <Button
                disabled={uploadMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                {uploadMutation.isPending ? "Uploading..." : "Upload Statement CSV"}
              </Button>
            </>
          ) : null
        }
      >
        <p className="text-sm text-slate-500">
          Review client-submitted manual payments, inspect candidate matches from imported
          statements, and confirm or reject each confirmation.
        </p>
        {feedback ? (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {feedback}
          </div>
        ) : null}
      </PageSection>

      <PageSection title="Filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FormField label="Search">
            <Input
              onChange={(event) => updateParam("search", event.target.value || null)}
              placeholder="Confirmation, client, loan, or reference"
              value={search}
            />
          </FormField>
          <FormField label="Queue">
            <Select
              onChange={(event) => updateParam("queue", event.target.value || null)}
              value={queue}
            >
              <option value="">All queues</option>
              <option value="AUTO_MATCH">Pending Auto-Match</option>
              <option value="REVIEW">Needs Review</option>
            </Select>
          </FormField>
          <FormField label="Status">
            <Select
              onChange={(event) => updateParam("status", event.target.value || null)}
              value={status}
            >
              <option value="">All statuses</option>
              <option value="PENDING_VERIFICATION">Pending Verification</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="REJECTED">Rejected</option>
            </Select>
          </FormField>
          <FormField label="Open item">
            <Input
              onChange={(event) => updateParam("selected_confirmation_id", event.target.value || null)}
              placeholder="Confirmation ID"
              value={selectedConfirmationId ?? ""}
            />
          </FormField>
        </div>
      </PageSection>

      <PageSection title="Manual Payment Confirmations">
        {manualPaymentsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading reconciliation queue...</p>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No manual payments found"
            description="Try changing the current filters or upload a new statement file."
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Confirmation</Th>
                <Th>Client</Th>
                <Th>Loan</Th>
                <Th>Amount</Th>
                <Th>Payment Date</Th>
                <Th>Queue</Th>
                <Th>Status</Th>
                <Th>Match</Th>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    className={selectedRow?.confirmationId === row.confirmationId ? "bg-blue-50" : ""}
                    key={row.confirmationId}
                    onClick={() => updateParam("selected_confirmation_id", row.confirmationId)}
                  >
                    <Td>
                      <div className="font-semibold text-slate-800">{row.confirmationId}</div>
                      <div className="text-slate-500">{row.transactionReference}</div>
                    </Td>
                    <Td>
                      <div className="font-medium text-slate-800">{row.clientName}</div>
                      <div className="text-slate-500">{row.clientId}</div>
                    </Td>
                    <Td>{row.loanId}</Td>
                    <Td>{formatCurrency(row.amount, row.currency)}</Td>
                    <Td>{formatDate(row.paymentDate)}</Td>
                    <Td>
                      <StatusPill tone={reviewTone(row)}>{reviewLabel(row)}</StatusPill>
                    </Td>
                    <Td>
                      <StatusPill tone={statusTone(row.status)}>
                        {formatEnumLabel(row.status)}
                      </StatusPill>
                    </Td>
                    <Td>
                      {row.matchedRepaymentId
                        ? row.matchedRepaymentId
                        : row.candidates.length > 0
                          ? `${row.candidates.length} candidate(s)`
                          : "No match yet"}
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>

            {meta ? (
              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>
                  Page {meta.page} of {meta.totalPages} · {meta.totalItems} confirmations
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    disabled={meta.page <= 1}
                    onClick={() => updateParam("page", String(meta.page - 1))}
                    type="button"
                    variant="secondary"
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => updateParam("page", String(meta.page + 1))}
                    type="button"
                    variant="secondary"
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </PageSection>

      <Drawer
        onClose={() => updateParam("selected_confirmation_id", null)}
        open={Boolean(selectedRow)}
        title={selectedRow?.confirmationId ?? "Reconciliation Detail"}
      >
        {selectedRow ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Client
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-800">{selectedRow.clientName}</p>
                <p className="text-sm text-slate-500">{selectedRow.clientId}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Loan
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-800">{selectedRow.loanId}</p>
                <p className="text-sm text-slate-500">{formatEnumLabel(selectedRow.paymentMethod)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Amount
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-800">
                  {formatCurrency(selectedRow.amount, selectedRow.currency)}
                </p>
                <p className="text-sm text-slate-500">{formatDate(selectedRow.paymentDate)}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <StatusPill tone={statusTone(selectedRow.status)}>
                    {formatEnumLabel(selectedRow.status)}
                  </StatusPill>
                  <StatusPill tone={reviewTone(selectedRow)}>{reviewLabel(selectedRow)}</StatusPill>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Submitted {formatDateTime(selectedRow.createdAt)}
                </p>
              </div>
            </div>

            <PageSection className="p-0 shadow-none" title="Candidate Matches">
              {selectedRow.candidates.length === 0 ? (
                <EmptyState
                  title="No candidate statements yet"
                  description="This confirmation stays pending until a matching statement row arrives or a reviewer confirms it manually."
                />
              ) : (
                <div className="space-y-3">
                  {selectedRow.candidates.map((candidate) => (
                    <label
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4"
                      key={candidate.statementRecordId}
                    >
                      <input
                        checked={selectedStatementRecordId === candidate.statementRecordId}
                        name="statement-record"
                        onChange={() => setSelectedStatementRecordId(candidate.statementRecordId)}
                        type="radio"
                      />
                      <div className="space-y-1 text-sm">
                        <div className="font-semibold text-slate-800">
                          {candidate.transactionReference}
                        </div>
                        <div className="text-slate-500">
                          {candidate.source} · {formatCurrency(candidate.amount, candidate.currency)} ·{" "}
                          {formatDate(candidate.paymentDate)}
                        </div>
                        <div className="text-slate-500">
                          Imported {formatDateTime(candidate.createdAt)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </PageSection>

            {selectedRow.manualReviewReason ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {selectedRow.manualReviewReason}
              </div>
            ) : null}

            {selectedRow.rejectionReason ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                Rejection reason: {selectedRow.rejectionReason}
              </div>
            ) : null}

            {canMutate && selectedRow.status === "PENDING_VERIFICATION" ? (
              <div className="grid grid-cols-1 gap-4">
                <FormField label="Confirmation note">
                  <Textarea
                    onChange={(event) => setConfirmNote(event.target.value)}
                    placeholder="Optional note for the repayment record"
                    rows={3}
                    value={confirmNote}
                  />
                </FormField>
                <FormField label="Rejection reason">
                  <Textarea
                    onChange={(event) => setRejectionReason(event.target.value)}
                    placeholder="Explain why this payment cannot be confirmed"
                    rows={3}
                    value={rejectionReason}
                  />
                </FormField>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={confirmMutation.isPending}
                    onClick={() => confirmMutation.mutate()}
                    type="button"
                  >
                    {confirmMutation.isPending ? "Confirming..." : "Confirm Payment"}
                  </Button>
                  <Button
                    disabled={rejectMutation.isPending}
                    onClick={() => rejectMutation.mutate()}
                    type="button"
                    variant="danger"
                  >
                    {rejectMutation.isPending ? "Rejecting..." : "Reject Payment"}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
